import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { Col, Row, units } from '@hbf/dsl';
import { FeaturedCountries } from 'ha/constants/FeaturedCountries';
import { useOptionalHermes } from 'ha/modules/Hermes';
import { KamernetBanner } from 'ha/modules/KamernetBanner';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';

import { CityInfo } from './CityInfo';
import { CityInfo as CityFAQ } from './CityFAQ';
import { SearchCategoriesContainer as SearchCategories } from '../containers/SearchCategories';
import { PagerContainer as SearchPager } from '../containers/SearchPager';
import { SearchCollectionSubtitleContainer as SearchCollectionSubtitle } from '../containers/SearchCollectionSubtitle';
import { SearchCitiesNearby } from './SearchCitiesNearby';
import { SearchPageBreadcrumbs } from './SearchPageBreadcrumbs';
import { HermesData } from '../types';

const BERLIN_CITY_PATHNAME = '/Berlin--Germany';

interface Props {
  cityCanonical?: string;
  countryCode?: string;
  shouldShowCitiesNearby: boolean;
  cityLocalizedName?: string;
}

const useStyles = makeStyles()(theme => ({
  seoMargin: {
    margin: units.yx(0.75, 0),
  },
  searchTips: {
    [theme.breakpoints.down('md')]: {
      padding: units.yx(0, 0.5),
    },
  },
}));

const SearchResultsBottom: React.FC<Props> = ({
  cityLocalizedName,
  shouldShowCitiesNearby,
  cityCanonical,
  countryCode,
}) => {
  const { classes, cx } = useStyles();
  const { data } = useOptionalHermes<HermesData>();
  const { searchKamernetBanner } = useFeatureFlags();

  const showSearchFAQs =
    data?.cityPagePathname?.toLowerCase() !==
    BERLIN_CITY_PATHNAME.toLowerCase();

  return (
    <Row>
      <Col xs={12} className={classes.seoMargin}>
        <SearchPager />
      </Col>
      {searchKamernetBanner === 'on' && (
        <Col xs={12}>
          <KamernetBanner
            cityCanonical={cityCanonical || ''}
            cityLocalized={cityLocalizedName || ''}
          />
        </Col>
      )}
      <Col xs={12} className={cx(classes.seoMargin, classes.searchTips)}>
        <SearchPageBreadcrumbs />
      </Col>
      <Col xs={12} className={classes.searchTips}>
        <SearchCollectionSubtitle />
      </Col>

      {showSearchFAQs && data?.searchFaq && data?.searchFaq?.length > 0 && (
        <Col xs={12} className={cx(classes.seoMargin, classes.searchTips)}>
          <CityFAQ faq={data?.searchFaq} />
        </Col>
      )}
      {data?.copyText && (
        <Col xs={12} className={cx(classes.seoMargin, classes.searchTips)}>
          <CityInfo text={data.copyText} />
        </Col>
      )}
      {!FeaturedCountries.some(country => country.code === countryCode) && (
        <React.Fragment>
          <Col xs={12} className={cx(classes.seoMargin, classes.searchTips)}>
            <SearchCategories />
          </Col>
          {shouldShowCitiesNearby && (
            <Col xs={12} className={cx(classes.seoMargin, classes.searchTips)}>
              <SearchCitiesNearby />
            </Col>
          )}
        </React.Fragment>
      )}
    </Row>
  );
};

export { SearchResultsBottom };
